import React, { type JSX } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import { SnackbarProvider, enqueueSnackbar, closeSnackbar, SnackbarKey } from 'notistack';
import { useTypedSelector, RootState } from '@fiji/common/src/app/store';
import { Button, useTheme } from '@mui/material';
import NotificationWithIcon from './NotificationWithIcon';
import { useTranslation } from 'react-i18next';

const isValidMessage = (message: unknown): message is string | React.ReactElement =>
    (typeof message === 'string' && Boolean(message.trim())) || React.isValidElement(message);

export const GlobalToast = (): JSX.Element => {
    const oktaAuthData = useOktaAuth();
    const { t } = useTranslation();
    const theme: any = useTheme();
    const toastData = useTypedSelector((state: RootState) => state.common.message);

    React.useEffect(() => {
        if (toastData?.message?.length) {
            if (toastData?.isUnauthorized) {
                oktaAuthData?.oktaAuth
                    ?.signOut()
                    .then()
                    .catch((error) => console.error('okta error', error));
            }
            const messages: unknown[] = Array.isArray(toastData.message) ? toastData.message : [toastData.message];
            messages.forEach((message: unknown) => {
                if (!isValidMessage(message)) {
                    console.error('Something went wrong.');
                    enqueueSnackbar(
                        t('COMMON:SOMETHING_WENT_WRONG', { defaultValue: 'Something went wrong' }),
                        toastData.extraArgs
                    );
                    return;
                }
                enqueueSnackbar(message, toastData.extraArgs);
            });
        } else if (toastData?.isOpen) {
            console.error('Something went wrong.');
            enqueueSnackbar(
                t('COMMON:SOMETHING_WENT_WRONG', { defaultValue: 'Something went wrong' }),
                toastData.extraArgs
            );
        }
    }, [toastData]);

    const renderSnackActionButton = (snackId: SnackbarKey): JSX.Element => (
        <Button
            size="small"
            sx={{ color: theme?.palette?.primary?.[200] }}
            onClick={(): void => closeSnackbar(snackId)}
        >
            OK
        </Button>
    );

    return (
        <SnackbarProvider
            autoHideDuration={toastData?.duration || 3000}
            maxSnack={9}
            action={(snackId: SnackbarKey): JSX.Element => renderSnackActionButton(snackId)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            preventDuplicate
            Components={{
                notificationWithIcon: NotificationWithIcon,
            }}
            SnackbarProps={{ 'data-testid': 'ToasterNotificationText' } as any}
        />
    );
};
